import { useState } from "react";
import { Table, Tag, Button, Modal, Form, Select, InputNumber, DatePicker, Popconfirm, message, Input, AutoComplete } from "antd";
import { Building2, Settings, UserPlus, LogOut } from "lucide-react";
import type { ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import {
  usePensionList, usePensionSettings, usePensionSettingsSave, usePensionEnrol, usePensionOptOut,
} from "../../../hooks/api/payslips/hooks";
import { useFieldWorkerList } from "../../../hooks/api/field-workers/hooks";

const formatDate = (v: any) => (v ? dayjs(v).format("DD MMM YYYY") : "—");

const enrolmentColor = (s: string) => {
  if (!s) return "default";
  const sl = s.toLowerCase();
  if (sl === "enrolled" || sl === "active" || sl === "opted-in" || sl === "opted_in") return "green";
  if (sl === "opted-out" || sl === "opted_out" || sl === "inactive") return "red";
  if (sl === "pending" || sl === "processing") return "orange";
  return "default";
};

function SettingsModal({ open, onClose, settings }: { open: boolean; onClose: () => void; settings: any }) {
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();

  const save = usePensionSettingsSave({
    onSuccess: (res) => { msgApi.success(res?.message || "Pension settings saved."); onClose(); },
    onError: (e) => msgApi.error(e?.response?.data?.message || "Failed"),
  });

  return (
    <Modal
      open={open}
      afterOpenChange={(isOpen) => {
        if (isOpen) {
          form.setFieldsValue({
            provider_name: settings?.provider_name,
            scheme_name: settings?.scheme_name,
            employer_pct: settings?.employer_pct != null ? Number(settings.employer_pct) : undefined,
            employee_pct: settings?.employee_pct != null ? Number(settings.employee_pct) : undefined,
            staging_date: settings?.staging_date ? dayjs(settings.staging_date) : undefined,
            re_enrolment_date: settings?.re_enrolment_date ? dayjs(settings.re_enrolment_date) : undefined,
          });
        }
      }}
      onCancel={onClose}
      title="Pension Scheme Settings"
      width={520}
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="save" type="primary" loading={save.isPending} onClick={() => form.submit()}>Save Settings</Button>,
      ]}
    >
      {ctx}
      <Form
        form={form}
        layout="vertical"
        size="middle"
        className="pt-2"
        onFinish={(vals) => {
          save.mutate({
            provider_name: vals.provider_name,
            scheme_name: vals.scheme_name,
            employer_pct: vals.employer_pct,
            employee_pct: vals.employee_pct,
            staging_date: vals.staging_date ? vals.staging_date.format("YYYY-MM-DD") : undefined,
            re_enrolment_date: vals.re_enrolment_date ? vals.re_enrolment_date.format("YYYY-MM-DD") : undefined,
          });
        }}
      >
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="provider_name" label="Provider" rules={[{ required: true, message: "Provider is required" }]}>
            <AutoComplete
              placeholder="e.g. NEST, Nowpensions…"
              options={["NEST", "NOW: Pensions", "The People's Pension", "Smart Pension", "Aviva"].map((p) => ({ value: p }))}
              filterOption={(v, o) => (o?.value as string)?.toLowerCase().includes(v.toLowerCase())}
            />
          </Form.Item>
          <Form.Item name="scheme_name" label="Scheme Name" rules={[{ required: true, message: "Scheme name is required" }]}>
            <Input placeholder="e.g. Workplace Pension" />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="employer_pct" label="Employer Contribution (%)" rules={[{ required: true, message: "Required" }]}>
            <InputNumber className="w-full" min={0} max={100} step={0.1} />
          </Form.Item>
          <Form.Item name="employee_pct" label="Employee Contribution (%)" rules={[{ required: true, message: "Required" }]}>
            <InputNumber className="w-full" min={0} max={100} step={0.1} />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="staging_date" label="Staging Date">
            <DatePicker className="w-full" />
          </Form.Item>
          <Form.Item name="re_enrolment_date" label="Re-Enrolment Date">
            <DatePicker className="w-full" />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
}

function EnrolModal({ open, onClose, settings }: { open: boolean; onClose: () => void; settings: any }) {
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();
  const { data: workerRes } = useFieldWorkerList({ page: 1, limit: 100 });
  const workers = (workerRes?.data as any[]) ?? [];

  const enrol = usePensionEnrol({
    onSuccess: (res) => { msgApi.success(res?.message || "Worker enrolled."); form.resetFields(); onClose(); },
    onError: (e) => msgApi.error(e?.response?.data?.message || "Failed"),
  });

  return (
    <Modal
      open={open}
      onCancel={() => { onClose(); form.resetFields(); }}
      title="Enrol Worker"
      width={460}
      footer={[
        <Button key="cancel" onClick={() => { onClose(); form.resetFields(); }}>Cancel</Button>,
        <Button key="save" type="primary" loading={enrol.isPending} onClick={() => form.submit()}>Enrol</Button>,
      ]}
    >
      {ctx}
      {!settings ? (
        <p className="text-amber-600 text-sm">Configure pension scheme settings before enrolling a worker.</p>
      ) : (
        <Form
          form={form}
          layout="vertical"
          size="middle"
          className="pt-2"
          onFinish={(vals) => {
            enrol.mutate({
              user_id: vals.user_id,
              pension_setting_id: settings.id,
              enrolment_date: vals.enrolment_date ? vals.enrolment_date.format("YYYY-MM-DD") : undefined,
            });
          }}
        >
          <Form.Item name="user_id" label="Worker" rules={[{ required: true, message: "Select a worker" }]}>
            <Select
              showSearch
              placeholder="Select worker…"
              filterOption={(v, o) => (o?.label as string)?.toLowerCase().includes(v.toLowerCase())}
              options={workers.map((w: any) => ({
                value: w.id,
                label: [w.first_name, w.last_name].filter(Boolean).join(" ") || w.email,
              }))}
            />
          </Form.Item>
          <Form.Item name="enrolment_date" label="Enrolment Date">
            <DatePicker className="w-full" placeholder="Today" />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}

const PensionPage = () => {
  const [page, setPage] = useState(1);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [enrolOpen, setEnrolOpen] = useState(false);
  const [msgApi, ctx] = message.useMessage();
  const limit = 20;

  const { data: settingsRes } = usePensionSettings();
  const settings = (settingsRes?.data as any) ?? null;

  const { data: res, isLoading } = usePensionList({ page, limit });
  const records: any[] = (res?.data as any) ?? [];
  const total = Number(res?.extra?.total ?? 0);

  const optOut = usePensionOptOut({
    onSuccess: () => msgApi.success("Worker opted out."),
    onError: (e: any) => msgApi.error(e?.response?.data?.message || "Failed"),
  });

  const columns: ColumnType<any>[] = [
    {
      title: "Worker",
      key: "worker",
      render: (_: any, r: any) =>
        r.worker_name || r.worker || r.name || r.employee_name || "—",
      width: 180,
    },
    {
      title: "Pension Scheme",
      key: "scheme",
      render: (_: any, r: any) => r.scheme_name || r.provider_name || "—",
      width: 180,
    },
    {
      title: "Enrolment Status",
      key: "status",
      render: (_: any, r: any) => {
        const s = r.status;
        return s ? (
          <Tag color={enrolmentColor(s)}>{String(s).toUpperCase().replace(/_/g, " ")}</Tag>
        ) : (
          "—"
        );
      },
      width: 150,
    },
    {
      title: "Employee %",
      key: "employee_pct",
      render: (_: any, r: any) => (r.employee_pct != null ? `${Number(r.employee_pct).toFixed(2)}%` : "—"),
      width: 110,
    },
    {
      title: "Employer %",
      key: "employer_pct",
      render: (_: any, r: any) => (r.employer_pct != null ? `${Number(r.employer_pct).toFixed(2)}%` : "—"),
      width: 110,
    },
    {
      title: "Enrolment Date",
      key: "enrolment_date",
      render: (_: any, r: any) => formatDate(r.enrolment_date ?? r.created_at),
      width: 130,
    },
    {
      title: "Opt-Out Date",
      key: "opt_out_date",
      render: (_: any, r: any) => formatDate(r.opt_out_date),
      width: 130,
    },
    {
      title: "",
      key: "actions",
      width: 90,
      render: (_: any, r: any) =>
        r.status === "enrolled" ? (
          <Popconfirm
            title="Opt this worker out of the pension scheme?"
            onConfirm={() => optOut.mutate(r.id)}
            okText="Opt Out"
            okType="danger"
          >
            <Button size="small" danger icon={<LogOut size={11} />} loading={optOut.isPending}>
              Opt Out
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  return (
    <div className="p-6 min-h-screen">
      {ctx}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Building2 size={20} className="text-slate-500" />
            <h1 className="text-xl font-semibold text-slate-800">Pension Auto-Enrolment</h1>
          </div>
          <p className="text-slate-500 text-sm mt-0.5">
            Workplace pension enrolment and contribution records
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button icon={<Settings size={14} />} onClick={() => setSettingsOpen(true)}>
            {settings ? "Edit Scheme Settings" : "Configure Scheme"}
          </Button>
          <ButtonWrapper type="primary" icon={<UserPlus size={15} />} onClick={() => setEnrolOpen(true)} className="flex items-center gap-2">
            Enrol Worker
          </ButtonWrapper>
        </div>
      </div>

      {settings && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 mb-4 flex items-center gap-6 flex-wrap text-sm">
          <div><span className="text-slate-400">Provider: </span><span className="font-medium text-slate-700">{settings.provider_name}</span></div>
          <div><span className="text-slate-400">Scheme: </span><span className="font-medium text-slate-700">{settings.scheme_name}</span></div>
          <div><span className="text-slate-400">Employer: </span><span className="font-medium text-slate-700">{Number(settings.employer_pct).toFixed(2)}%</span></div>
          <div><span className="text-slate-400">Employee: </span><span className="font-medium text-slate-700">{Number(settings.employee_pct).toFixed(2)}%</span></div>
          {settings.staging_date && <div><span className="text-slate-400">Staging: </span><span className="font-medium text-slate-700">{formatDate(settings.staging_date)}</span></div>}
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <Table
          dataSource={records}
          columns={columns}
          rowKey={(r) => r.id ?? r._id ?? Math.random()}
          loading={isLoading}
          locale={{
            emptyText: (
              <div className="py-12 text-center text-slate-400">
                <Building2 size={40} className="mx-auto mb-3 opacity-30" />
                <p className="font-medium">No pension records found</p>
                <p className="text-sm mt-1">
                  Pension auto-enrolment data will appear here once records are created.
                </p>
              </div>
            ),
          }}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showTotal: (t) => `${t} records`,
            showSizeChanger: false,
            onChange: (p) => setPage(p),
          }}
          scroll={{ x: 1080 }}
          size="small"
        />
      </div>

      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} settings={settings} />
      <EnrolModal open={enrolOpen} onClose={() => setEnrolOpen(false)} settings={settings} />
    </div>
  );
};

export default PensionPage;
